import React from 'react';
import LandingLayout from 'layouts/LandingLayout/LandingLayout';
import styles from './LifetimeDeal.module.scss';

// Sections
import HeroSection from './sections/HeroSection';
import PricingSection from './sections/PricingSection';
import TestimonialsSection from './sections/TestimonialsSection';
import FeaturesHeaderSection from './sections/FeaturesHeaderSection';
import FeatureBlockSection from './sections/FeatureBlockSection';
import VIPCommunitySection from './sections/VIPCommunitySection';
import HowItWorksSection from './sections/HowItWorksSection';
import PublishSection from './sections/PublishSection';
import FAQSection from './sections/FAQSection';
import FinalCTASection from './sections/FinalCTASection';

// Feature icons
const AIIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M10 2V18M2 10H18M4.93 4.93L15.07 15.07M15.07 4.93L4.93 15.07" stroke="#475467" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const CalendarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect x="3" y="4" width="14" height="13" rx="2" stroke="#475467" strokeWidth="1.5"/>
    <path d="M3 8H17M7 2V6M13 2V6" stroke="#475467" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const GlobeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="7.5" stroke="#475467" strokeWidth="1.5"/>
    <path d="M2.5 10H17.5M10 2.5C12.5 5 13.5 7.5 13.5 10C13.5 12.5 12.5 15 10 17.5C7.5 15 6.5 12.5 6.5 10C6.5 7.5 7.5 5 10 2.5Z" stroke="#475467" strokeWidth="1.5"/>
  </svg>
);

const ChartIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M3 17V10M8 17V6M13 17V8M18 17V3" stroke="#475467" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const BrandIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M10 2L12.5 7L18 8L14 12L15 18L10 15L5 18L6 12L2 8L7.5 7L10 2Z" stroke="#475467" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ScanIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M2 6V4C2 3 3 2 4 2H6M14 2H16C17 2 18 3 18 4V6M18 14V16C18 17 17 18 16 18H14M6 18H4C3 18 2 17 2 16V14M2 10H18" stroke="#475467" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const ChatIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M3 5C3 3.9 3.9 3 5 3H15C16.1 3 17 3.9 17 5V12C17 13.1 16.1 14 15 14H8L4 17V14H5C3.9 14 3 13.1 3 12V5Z" stroke="#475467" strokeWidth="1.5"/>
  </svg>
);

const KanbanIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect x="2" y="3" width="4" height="14" rx="1" stroke="#475467" strokeWidth="1.5"/>
    <rect x="8" y="3" width="4" height="10" rx="1" stroke="#475467" strokeWidth="1.5"/>
    <rect x="14" y="3" width="4" height="7" rx="1" stroke="#475467" strokeWidth="1.5"/>
  </svg>
);

const UsersIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <circle cx="7" cy="6" r="3" stroke="#475467" strokeWidth="1.5"/>
    <path d="M2 17C2 14.2 4.2 12 7 12C9.8 12 12 14.2 12 17" stroke="#475467" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="14" cy="7" r="2" stroke="#475467" strokeWidth="1.5"/>
    <path d="M13 12C14.7 12 16.2 13 17 14.5" stroke="#475467" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const IntegrationIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="2" stroke="#475467" strokeWidth="1.5"/>
    <circle cx="4" cy="4" r="2" stroke="#475467" strokeWidth="1.5"/>
    <circle cx="16" cy="4" r="2" stroke="#475467" strokeWidth="1.5"/>
    <circle cx="16" cy="16" r="2" stroke="#475467" strokeWidth="1.5"/>
    <circle cx="4" cy="16" r="2" stroke="#475467" strokeWidth="1.5"/>
    <path d="M6 6L8 8M12 8L14 6M14 14L12 12M8 12L6 14" stroke="#475467" strokeWidth="1.5"/>
  </svg>
);

const TemplateIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect x="2" y="2" width="16" height="16" rx="2" stroke="#475467" strokeWidth="1.5"/>
    <path d="M2 7H18M7 7V18" stroke="#475467" strokeWidth="1.5"/>
  </svg>
);

const LifetimeDeal = () => {
  const handleGetLifetimeAccess = (plan = 'pro') => {
    localStorage.setItem('lifetimeDealIntent', 'true');
    if (plan === 'business') {
      window.location.href = '/payment?lifetime=true&plan=business';
    } else {
      window.location.href = '/payment?lifetime=true';
    }
  };

  // Feature block data
  const featureBlocks = [
    {
      title: 'Generate a full content pipeline in minutes',
      description: 'Create high converting content campaigns across multiple channels with one click, in minutes - with built-in content calendar, briefs, drafts, and trello board to track and manage your posts.',
      featuresLabel: 'features',
      features: [
        { icon: <AIIcon />, text: '30-day content calendar' },
        { icon: <ChartIcon />, text: 'Auto-populated briefs' },
        { icon: <GlobeIcon />, text: 'Multi-language support' },
        { icon: <BrandIcon />, text: 'Tailored to your brand' },
      ],
      image: '/Images/ContentPipeline.png',
      imageAlt: 'Content Pipeline - Kanban Board',
      reverse: false,
    },
    {
      title: 'Content that sounds exactly like you',
      description: 'Vocable analyzes your website and brand profile to understand your tone, industry, messaging, and offers to write brand relevant content that sounds like you.',
      featuresLabel: 'features',
      features: [
        { icon: <ScanIcon />, text: 'Website scanner' },
        { icon: <AIIcon />, text: 'AI-powered briefs' },
        { icon: <BrandIcon />, text: 'Brand intelligence' },
        { icon: <ChatIcon />, text: 'Multi-AI assistance' },
      ],
      image: '/Images/BrandVoice.png',
      imageAlt: 'Brand Voice - Content List',
      reverse: true,
    },
    {
      title: 'Plan, edit, and manage everything in one place',
      description: 'See your entire content workflow at a glance and stay organized from campaign to campaign with multiple views.',
      featuresLabel: 'features',
      features: [
        { icon: <KanbanIcon />, text: 'Calendar & list views' },
        { icon: <BrandIcon />, text: 'Multiple brands' },
        { icon: <UsersIcon />, text: 'Team collaboration' },
        { icon: <IntegrationIcon />, text: 'Channel integrations' },
      ],
      image: '/Images/ContentManagement.png',
      imageAlt: 'Content Management - Calendar View',
      reverse: false,
    },
    {
      title: 'Create content on the fly with proven AI templates',
      description: 'Access 100+ of research-backed templates built from real storytelling frameworks, viral post patterns, and platform-specific best practices.',
      featuresLabel: 'features',
      features: [
        { icon: <TemplateIcon />, text: '100+ prebuilt prompts' },
        { icon: <IntegrationIcon />, text: 'Channel-specific' },
        { icon: <ChartIcon />, text: 'Tested frameworks' },
        { icon: <AIIcon />, text: 'Editable & flexible' },
      ],
      image: '/Images/AITemplates.png',
      imageAlt: 'AI Templates',
      reverse: true,
    },
  ];

  return (
    <LandingLayout>
      <div className={styles.page}>
        {/* Simple logo header */}
        <header className={styles.header}>
          <div className={styles.headerContainer}>
            <a href="https://vocable.ai" className={styles.logoLink}>
              <img
                src="/Images/vocablebalck_greeen.svg"
                alt="Vocable"
                className={styles.logo}
              />
            </a>
          </div>
        </header>

        <main className={styles.main}>
          <HeroSection onGetAccess={() => handleGetLifetimeAccess('pro')} />

          <PricingSection
            onGetAccessPro={() => handleGetLifetimeAccess('pro')}
            onGetAccessBusiness={() => handleGetLifetimeAccess('business')}
          />

          <TestimonialsSection />

          <FeaturesHeaderSection />

          {featureBlocks.map((feature, index) => (
            <FeatureBlockSection
              key={index}
              title={feature.title}
              description={feature.description}
              features={feature.features}
              featuresLabel={feature.featuresLabel}
              image={feature.image}
              imageAlt={feature.imageAlt}
              video={feature.video}
              videoPoster={feature.videoPoster}
              reverse={feature.reverse}
              backgroundColor={index % 2 === 0 ? '#FFFFFF' : '#F9FAFB'}
            />
          ))}

          <VIPCommunitySection onGetAccess={() => handleGetLifetimeAccess('pro')} />

          <HowItWorksSection />

          <PublishSection onGetAccess={() => handleGetLifetimeAccess('pro')} />

          <FAQSection />

          <FinalCTASection onGetAccess={() => handleGetLifetimeAccess('pro')} />

          <footer className={styles.footer}>
            <div className={styles.footerContent}>
              <div className={styles.footerLeft}>
                <img
                  src="/Images/vocablebalck_greeen.svg"
                  alt="Vocable"
                  className={styles.footerLogo}
                />
                <span className={styles.footerCopyright}>Copyright © Vocable AI {new Date().getFullYear()}</span>
              </div>
              <div className={styles.footerRight}>
                <a href="https://vocable.ai/blog" className={styles.footerLink}>Blog</a>
                <a href="https://vocable.ai/terms" className={styles.footerLink}>Terms & Conditions</a>
                <a href="https://vocable.ai/privacy" className={styles.footerLink}>Privacy Policy</a>
              </div>
            </div>
          </footer>
        </main>
      </div>
    </LandingLayout>
  );
};

export default LifetimeDeal;
